import { StickyNote } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { getErrorMessage } from "../../lib/query/errors";
import { useReadOnly } from "../../stores/approvalStore";
import { useAnalystNotes } from "./api/useAnalystNotes";
import { useSaveAnalystNote } from "./api/useSaveAnalystNote";
import { formatNoteDatetime } from "./formatNoteDatetime";

interface AnalystNotesPanelProperties {
  approvalId: string;
  /** Closes the enclosing modal — invoked on Cancel and after a successful save. */
  onClose: () => void;
}

/*
 * Analyst Notes modal body (OR-22625). Lists the recap-service analyst notes
 * recorded against the deal — Entry Date / Notes / User — newest-first (server
 * order), no pagination, and lets a CAC employee add a new note.
 *
 * The notes list header stays pinned via `sticky` so column labels remain
 * visible on long histories; the list region scrolls independently of the
 * add-note form below it. Writing is gated by `useReadOnly()` (disabled when the
 * deal isn't held by the current user); the BFF is the real enforcement point.
 */
function AnalystNotesPanel({ approvalId, onClose }: AnalystNotesPanelProperties) {
  const { t } = useTranslation();
  const readOnly = useReadOnly();
  const { data, isLoading, isError, error } = useAnalystNotes(approvalId);
  const saveNote = useSaveAnalystNote(approvalId);
  const [newNote, setNewNote] = useState("");

  const trimmed = newNote.trim();
  const canSave = trimmed.length > 0 && !readOnly && !saveNote.isPending;

  const handleSave = () => {
    if (!canSave) {
      return;
    }
    saveNote.mutate(trimmed, {
      onSuccess: () => {
        setNewNote("");
        onClose();
      },
    });
  };

  const rows = data ?? [];

  return (
    <div className="flex flex-col">
      {/* Notes list */}
      <div className="max-h-96 overflow-y-auto p-2">
        {isLoading ? (
          <p role="status" className="text-muted-foreground px-1 py-3 text-xs">
            {t("analystNotes.loading")}
          </p>
        ) : isError ? (
          <p role="alert" className="text-destructive px-1 py-3 text-xs">
            {t("analystNotes.loadError", { message: getErrorMessage(error) })}
          </p>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center py-8 text-center">
            <StickyNote className="text-border mb-3 size-12" aria-hidden="true" />
            <p className="text-muted-foreground text-xs">{t("analystNotes.empty")}</p>
          </div>
        ) : (
          // No `overflow-hidden` on the wrapper: an overflow clip between the
          // sticky <thead> and the scrolling list would cancel the sticky header.
          <div className="border-border rounded-lg border">
            <table className="w-full border-collapse text-xs">
              <caption className="sr-only">{t("analystNotes.caption")}</caption>
              <thead>
                {/* Background + sticky live on each <th>, not the <tr>: under
                    `border-collapse` Chrome does not paint a <tr>/<thead>
                    background for a sticky header, so scrolled rows show through. */}
                <tr className="text-primary">
                  <th
                    scope="col"
                    className="bg-section-header border-border sticky top-0 border-b px-2 py-1.5 text-left font-semibold"
                  >
                    {t("analystNotes.columns.entryDate")}
                  </th>
                  <th
                    scope="col"
                    className="bg-section-header border-border sticky top-0 border-b px-2 py-1.5 text-left font-semibold"
                  >
                    {t("analystNotes.columns.notes")}
                  </th>
                  <th
                    scope="col"
                    className="bg-section-header border-border sticky top-0 border-b px-2 py-1.5 text-left font-semibold"
                  >
                    {t("analystNotes.columns.user")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  // Notes have no stable server id; the newest-first ordering is
                  // fixed for a given fetch, so the index is a stable key within a
                  // render pass. Safe here because the list is read-only.
                  <tr key={index} className="border-border border-t">
                    <td className="text-primary whitespace-nowrap px-2 py-1.5 align-top">
                      {formatNoteDatetime(row.createdDatetime)}
                    </td>
                    <td className="text-primary px-2 py-1.5 align-top">{row.note}</td>
                    <td className="text-primary whitespace-nowrap px-2 py-1.5 align-top">{row.authorUserId}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add note */}
      <div className="border-border border-t p-4">
        <label htmlFor="analyst-notes-new" className="text-primary mb-2 block text-xs font-semibold">
          {t("analystNotes.addNote.label")}
        </label>
        <Textarea
          id="analyst-notes-new"
          value={newNote}
          onChange={(event) => setNewNote(event.target.value)}
          placeholder={t("analystNotes.addNote.placeholder")}
          disabled={readOnly || saveNote.isPending}
          className="min-h-20 resize-none"
        />
        {saveNote.isError && (
          <p role="alert" className="text-destructive mt-2 text-xs">
            {t("analystNotes.saveError", { message: getErrorMessage(saveNote.error) })}
          </p>
        )}
      </div>

      {/* Footer */}
      <div className="bg-muted flex justify-end gap-2 border-t px-4 py-3">
        <Button type="button" variant="outline" size="sm" disabled={saveNote.isPending} onClick={onClose}>
          {t("analystNotes.addNote.cancel")}
        </Button>
        <Button type="button" size="sm" disabled={!canSave} onClick={handleSave}>
          {t("analystNotes.addNote.save")}
        </Button>
      </div>
    </div>
  );
}

export default AnalystNotesPanel;
